import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, MessageSquareWarning, ClipboardList, MapPin, Clock, Users2, Bell } from "lucide-react";
import Shell from "@/components/Shell";
import { Panel, Btn, Field, Input, TextArea, Select, StatusBadge } from "@/components/kit";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import api, { formatApiError } from "@/lib/api";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: Bell },
  { key: "book", label: "Book a Service", icon: CalendarCheck },
  { key: "bookings", label: "My Bookings", icon: ClipboardList },
  { key: "complain", label: "Raise a Request", icon: MessageSquareWarning },
  { key: "requests", label: "Track Requests", icon: ClipboardList },
];

export default function CustomerPortal() {
  const [active, setActive] = useState("dashboard");
  const [locations, setLocations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [complaints, setComplaints] = useState([]);

  const load = () => {
    api.get("/public/locations").then((r) => setLocations(r.data));
    api.get("/bookings/mine").then((r) => setBookings(r.data));
    api.get("/complaints/mine").then((r) => setComplaints(r.data));
  };
  useEffect(load, []);

  return (
    <Shell title="Guest Experience" subtitle="Book services and track your requests" nav={NAV} active={active} onNav={setActive}>
      {active === "dashboard" && <NotificationsPanel title="Your updates" intro="Acknowledgements, approvals and resolutions on your requests appear here." />}
      {active === "book" && <BookTab locations={locations} onDone={() => { load(); setActive("bookings"); }} />}
      {active === "bookings" && <BookingsTab bookings={bookings} />}
      {active === "complain" && <ComplainTab locations={locations} onDone={() => { load(); setActive("requests"); }} />}
      {active === "requests" && <RequestsTab complaints={complaints} />}
    </Shell>
  );
}

function BookTab({ locations, onDone }) {
  const [locId, setLocId] = useState("");
  const [services, setServices] = useState([]);
  const [svcId, setSvcId] = useState("");
  const [form, setForm] = useState({ date: "", time: "", party_size: 2, notes: "", allergy_info: "" });
  const [busy, setBusy] = useState(false);
  const svc = services.find((s) => s.id === svcId);

  useEffect(() => {
    if (!locId) return setServices([]);
    api.get(`/public/services?location_id=${locId}`).then((r) => { setServices(r.data); setSvcId(""); });
  }, [locId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bookings", { location_id: locId, service_id: svcId, ...form, party_size: Number(form.party_size) });
      toast.success("Booking requested! Reception will confirm shortly.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Panel className="p-6 sm:p-8 max-w-2xl">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Location">
          <Select data-testid="book-location" required value={locId} onChange={(e) => setLocId(e.target.value)}>
            <option value="">Choose a location…</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.city}</option>)}
          </Select>
        </Field>
        {locId && (
          <Field label="Service">
            <Select data-testid="book-service" required value={svcId} onChange={(e) => setSvcId(e.target.value)}>
              <option value="">Choose a service…</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name} · cap {s.capacity}</option>)}
            </Select>
          </Field>
        )}
        {svc && <p className="text-sm text-purple-300/70 -mt-2">{svc.description} · {svc.department_name}</p>}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Date"><Input data-testid="book-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Time"><Input data-testid="book-time" type="time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></Field>
        </div>
        <Field label="Party size"><Input data-testid="book-party" type="number" min="1" required value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} /></Field>
        <Field label="Notes (optional)"><TextArea data-testid="book-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Occasion, seating preference…" /></Field>
        {svc?.food_related && (
          <Field label="Allergies / dietary requirements" hint="This will be flagged to Kitchen & F&B and must be acknowledged before service.">
            <TextArea data-testid="book-allergy" value={form.allergy_info} onChange={(e) => setForm({ ...form, allergy_info: e.target.value })} placeholder="e.g. Severe nut allergy, vegan" />
          </Field>
        )}
        <Btn type="submit" disabled={busy || !svcId} data-testid="book-submit">{busy ? "Booking…" : "Request booking"}</Btn>
      </form>
    </Panel>
  );
}

function BookingsTab({ bookings }) {
  if (!bookings.length) return <Empty text="No bookings yet." />;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {bookings.map((b) => (
        <Panel key={b.id} className="p-5" data-testid={`booking-${b.id}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div><h3 className="font-head font-bold text-white">{b.service_name}</h3><p className="text-xs text-purple-400">{b.department_name}</p></div>
            <StatusBadge status={b.status} />
          </div>
          <div className="space-y-1.5 text-sm text-purple-200/80">
            <div className="flex items-center gap-2"><Clock size={14} /> {b.date} at {b.time}</div>
            <div className="flex items-center gap-2"><Users2 size={14} /> Party of {b.party_size}</div>
            {b.allergy_info && <div className="text-rose-300 text-xs mt-1">Allergy: {b.allergy_info} {b.allergy_ack ? "(acknowledged)" : "(pending)"}</div>}
            {b.notes && <p className="text-xs text-purple-400/70 pt-1">"{b.notes}"</p>}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function ComplainTab({ locations, onDone }) {
  const [form, setForm] = useState({ location_id: "", subject: "", description: "", category: "General" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/complaints", form);
      toast.success(`Request submitted — reference ${r.data.reference}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-6 sm:p-8 max-w-2xl">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Location">
          <Select data-testid="comp-location" required value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
            <option value="">Choose a location…</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.city}</option>)}
          </Select>
        </Field>
        <Field label="Category">
          <Select data-testid="comp-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {["General", "Food & Beverage", "Housekeeping", "Maintenance", "Reception", "Spa", "Noise"].map((c) => <option key={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Subject"><Input data-testid="comp-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Short summary" /></Field>
        <Field label="Describe what happened"><TextArea data-testid="comp-description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <Btn type="submit" disabled={busy} data-testid="comp-submit">{busy ? "Submitting…" : "Submit request"}</Btn>
      </form>
    </Panel>
  );
}

function RequestsTab({ complaints }) {
  if (!complaints.length) return <Empty text="No requests submitted yet." />;
  return (
    <div className="space-y-4">
      {complaints.map((c) => (
        <Panel key={c.id} className="p-5" data-testid={`request-${c.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="flex items-center gap-2"><h3 className="font-head font-bold text-white">{c.subject}</h3><code className="text-[11px] text-amber-300">{c.reference}</code></div>
              <p className="text-xs text-purple-400 mt-0.5"><MapPin size={11} className="inline" /> {c.location_name} · {c.category}</p>
            </div>
            <StatusBadge status={c.status} />
          </div>
          <p className="text-sm text-purple-200/80">{c.description}</p>
          {c.acknowledged && !c.resolution_note && <p className="text-xs text-sky-300 mt-2">✓ Acknowledged by staff — being handled.</p>}
          {c.resolution_note && (
            <div className="mt-3 p-3 rounded-xl bg-[#230A4A] border-l-2 border-[#FF2A6D]">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-300/60 mb-1">Resolution</div>
              <p className="text-sm text-purple-100">{c.resolution_note}</p>
            </div>
          )}
        </Panel>
      ))}
    </div>
  );
}

function Empty({ text }) {
  return <Panel className="p-12 text-center text-purple-400/70">{text}</Panel>;
}
